import { AccountingEntry } from '@adornis/accounting/base/entities/AccountingEntry.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { Group } from '../../groups/entities/Group.js';

export enum QuittungDownloadType {
  SPENDER_QUITTUNG = 'Spenderquittung',
  SB_QUITTUNG = 'Sorgeberechtigterquittung',
}

/**
 * @entity Quittung
 */
@Entity()
export class MartinusAccountingEntry extends AccountingEntry {
  static override _class = 'MartinusAccountingEntry';

  constructor(doc: PartialEntityData<MartinusAccountingEntry>) {
    super(doc);
  }

  /**
   * The schools the source file belongs to: the school of the folder plus the schools booked on the same
   * accounting number. Owned by the import (the folder mapping decides it, not the row) — the resolution only
   * reads it, as the scope of legacy debitor numbers and as the references every entry carries.
   */
  @Field(() => [ID], { default: v => v ?? [] })
  schoolIDs: Maybe<string[]>;

  /** loaded instead of stored names, so a renamed school is never stale here; query `schoolIDs` along with it */
  // @ts-expect-error _id as object id in mongo
  @DataLoadedField(() => [Group], ({ schoolIDs }) => schoolIDs)
  schools: Maybe<Group[]>;

  @Field(() => String)
  kontoinhaber: Maybe<string>;

  @Field(() => String)
  iban: Maybe<string>;

  @Field(() => String)
  importError: Maybe<string>;

  @Field(() => Boolean, { default: v => v ?? false })
  canBeDownloadedAsQuittung: Maybe<boolean>;

  @Field(() => Boolean, { default: v => v ?? false })
  returnOrder: Maybe<boolean>;

  @Field(() => String)
  tag: Maybe<string>;

  /** The {@link AccountingImportFile} the entry was imported from. */
  @Field(() => String)
  sourceFileID: Maybe<string>;

  /** Position of the row in its file — the order the reference resolution replays a school in. */
  @Field(() => Int)
  rowIndex: Maybe<number>;

  /**
   * What the file itself says, written by the import and never touched again. The resolver derives
   * `iban` / `kontoinhaber` / `importError` from these plus its lookups, so resolving an entry twice is
   * always safe.
   */
  @Field(() => String)
  csvIban: Maybe<string>;

  @Field(() => String)
  csvKontoinhaber: Maybe<string>;

  @Field(() => String)
  parseError: Maybe<string>;

  /**
   * DATEV's `Buchungs GUID` of a Buchungsstapel row. It names the booking, not the row: the 2–3 rows a booking
   * is exported as (own leg, mirror leg, Sammelkonto leg) share it, and it survives every edit the accountant
   * makes to the booking. The import recognises an existing entry by it.
   */
  @Field(() => String)
  guid: Maybe<string>;

  /** The bank's `Transaktions-ID` of a Kontoauszug row — unique per row, the import recognises an entry by it. */
  @Field(() => String)
  transactionID: Maybe<string>;

  /** The `Mandats-ID` of a Kontoauszug row: for a Lastschrift DATEV writes the debitor number plus five digits. */
  @Field(() => String)
  mandatsID: Maybe<string>;

  /** When the reference resolution last changed this entry; empty until it ran for the first time. */
  @Field(() => Date)
  resolvedAt: Maybe<Date>;
}
